import { useQuery } from '@tanstack/react-query';

import { useIsChecklessPaymentsEnabled } from 'reservations/account/providers/ConfigProvider';
import { useHasFeePolicy } from 'reservations/account/providers/ConfigProvider';
import { useLocationId } from 'reservations/account/providers/ConfigProvider';
import { BENTOBOX_BOOKING_ENDPOINT } from 'reservations/base/constants/endpoints';
import { MINUTE_IN_MILLISECONDS } from 'reservations/base/constants/time';
import { buildBentoboxApiEndpoint } from 'reservations/base/helpers/buildBentoboxApiEndpoint';
import { generateDummyBooking } from 'reservations/booking/helpers';
import { BookingQueryResponseSchema } from 'reservations/booking/types';
import { usePreviewModeContext } from 'reservations/preview/providers/PreviewModeProvider';
import { restClient } from 'utility/restClient';

const DEFAULT_QUERY_OPTIONS = {
  staleTime: MINUTE_IN_MILLISECONDS * 5,
  gcTime: MINUTE_IN_MILLISECONDS * 5,
  refetchInterval: MINUTE_IN_MILLISECONDS * 5
};

async function fetchBooking(locationId: string | number, bookingId: string) {
  const options: Record<string, any> = {
    location_id: locationId,
    booking_id: bookingId
  };
  const endpoint = buildBentoboxApiEndpoint(BENTOBOX_BOOKING_ENDPOINT, options);
  const response = await restClient.get(endpoint);

  try {
    return BookingQueryResponseSchema.parse(response);
  } catch (err) {
    console.error(`fetchBooking: ${err}`);
    throw err;
  }
}

export const useBookingQuery = (
  accessToken: string,
  bookingId: string,
  hasVisitId: boolean
) => {
  const { isPreview } = usePreviewModeContext();
  const hasFeePolicy = useHasFeePolicy();
  const locationId = useLocationId();
  const isChecklessEnabled = useIsChecklessPaymentsEnabled();

  return useQuery({
    ...DEFAULT_QUERY_OPTIONS,
    queryKey: ['bentoboxReservations', 'booking', bookingId],
    queryFn: () => {
      if (isPreview) {
        return Promise.resolve(
          generateDummyBooking(hasFeePolicy, isChecklessEnabled)
        );
      }
      return fetchBooking(locationId, bookingId);
    },
    enabled: !hasVisitId,
    retry: false
  });
};
